import React from 'react';

import { Form } from '../../components/Form';
import { InputNumberField } from '../../components/InputNumberField';
import { InputWithOptions } from '../../components/InputWithOptions';
import { EFieldName } from '../../constants';
import { calculatePercentageOfObjectCost } from '../../utils';

import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { ProgramsField } from 'shared/mortgageMiniCalculator/components/ProgramsField';
import { IFetchOffersProps } from '../../hooks/useGetOffers';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { ETypeChangeInput } from '../../utils/tracking/types';
import {
  LOAN_TERM_OPTIONS,
  MAX_DOWN_PAYMENT_PERCENTAGE,
  MAX_LOAN_TERM,
  MAX_OBJECT_COST,
  MIN_DOWN_PAYMENT_PERCENTAGE,
  MIN_LOAN_TERM,
  MIN_OBJECT_COST,
} from './constants';
import { useFormCalculator } from './hooks/useFormCalculator';

interface IFormContainerProps {
  fetchOffers: (params: IFetchOffersProps) => void;
}

export const FormContainer: React.FC<IFormContainerProps> = ({ fetchOffers }) => {
  const {
    objectCost,
    downPayment,
    loanTermYears,
    downPaymentOptions,
    downPaymentChipValue,
    getHandlerByFieldName,
    benefitProgram,
  } = useFormCalculator(fetchOffers);
  const effectiveObjectCost = objectCost > 0 ? objectCost : MIN_OBJECT_COST;
  const isDesktop = useIsDesktop();
  const { config } = useApplicationContext();
  const isProgramFilterFieldEnabled = config.get<boolean>(ERuntimeSettings.programFilterFieldEnabled) ?? false;

  return (
    <Form>
      <InputNumberField
        label={'Стоимость недвижимости'}
        value={objectCost}
        name={EFieldName.ObjectCost}
        onChange={getHandlerByFieldName(EFieldName.ObjectCost, ETypeChangeInput.MANUAL)}
        rightAdornment={'₽'}
        min={MIN_OBJECT_COST}
        max={MAX_OBJECT_COST}
      />
      {isDesktop && isProgramFilterFieldEnabled && (
        <ProgramsField
          key={benefitProgram}
          value={benefitProgram}
          onChange={getHandlerByFieldName(EFieldName.benefitProgram, ETypeChangeInput.AUTO)}
        />
      )}
      <InputWithOptions
        label={'Первый взнос'}
        value={downPayment}
        name={EFieldName.DownPayment}
        options={downPaymentOptions}
        chipValue={downPaymentChipValue}
        onChange={getHandlerByFieldName(EFieldName.DownPayment, ETypeChangeInput.MANUAL)}
        onClickChip={getHandlerByFieldName(EFieldName.DownPayment, ETypeChangeInput.AUTO)}
        rightAdornment={'₽'}
        min={calculatePercentageOfObjectCost(effectiveObjectCost, MIN_DOWN_PAYMENT_PERCENTAGE)}
        max={calculatePercentageOfObjectCost(effectiveObjectCost, MAX_DOWN_PAYMENT_PERCENTAGE)}
      />
      <InputWithOptions
        label={'Срок кредита'}
        value={loanTermYears}
        name={EFieldName.loanTermYears}
        options={LOAN_TERM_OPTIONS}
        chipValue={loanTermYears}
        onChange={getHandlerByFieldName(EFieldName.loanTermYears, ETypeChangeInput.MANUAL)}
        onClickChip={getHandlerByFieldName(EFieldName.loanTermYears, ETypeChangeInput.AUTO)}
        rightAdornment={'лет'}
        min={MIN_LOAN_TERM}
        max={MAX_LOAN_TERM}
      />
      {!isDesktop && isProgramFilterFieldEnabled && (
        <ProgramsField
          key={benefitProgram}
          value={benefitProgram}
          onChange={getHandlerByFieldName(EFieldName.benefitProgram, ETypeChangeInput.AUTO)}
        />
      )}
    </Form>
  );
};

FormContainer.displayName = 'FormContainer';
